import {
  ChangeDetectorRef,
  HostListener,
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Inject,
} from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

import { SystemSettingService } from '@app/api/systemsettting.service';

@Component({
  selector: 'app-modal-edit-footer.component',
  templateUrl: './app-modal-edit-footer.component.html',
  styleUrls: ['./app-modal-edit-footer.component.scss'],
})
export class AppModalFooterManagementComponent implements OnInit {
  type: 'newGroup' | 'newGroupChild' | 'updateGroup' | 'updateGroupChild';
  title: string;
  id: string;
  lang: 'vi' | 'en' | 'zh';

  systemForm = new FormGroup({
    id: new FormControl({ value: '', disabled: true }),
    type: new FormControl(''),
    name: new FormControl('', [Validators.required]),
    value: new FormControl(''),
    description: new FormControl(''),
    is_hidden: new FormControl(false),
    sort_order: new FormControl(0, [Validators.min(0), Validators.pattern(/^[0-9]+$/)]),
    parent_id: new FormControl(''),
    page_location: new FormControl(''),
    slug: new FormControl(''), // ✅ Bỏ validator ban đầu
    language: new FormControl(''),
  });

  constructor(
    private dialogRef: MatDialogRef<AppModalFooterManagementComponent>,
    private toastr: ToastrService,
    private system: SystemSettingService,
    @Inject(MAT_DIALOG_DATA)
    public data: { title: string; type: any; id: string; lang: 'vi' | 'en' | 'zh' }
  ) {
    this.type = data.type;
    this.title = data.title;
    this.id = data.id;
    this.lang = data.lang;
  }

  ngOnInit() {
    // ✅ Lắng nghe thay đổi type để update validators
    this.systemForm.get('type').valueChanges.subscribe((type) => {
      const valueControl = this.systemForm.get('value');
      const slugControl = this.systemForm.get('slug');
      const nameControlValue = this.systemForm.get('name').value;

      if (type === 'external_link') {
        valueControl.setValidators([Validators.required, Validators.pattern('^https?://.+')]);
        slugControl.clearValidators(); // External link không cần slug
        slugControl.setValue(''); // ✅ Clear slug khi không cần
      } else if (type === 'policy') {
        valueControl.setValidators([Validators.required, Validators.pattern('^(?!\\s*$).+')]);

        // ✅ Validator slug theo ngôn ngữ
        if (this.lang === 'zh') {
          // Tiếng Trung: cho phép ký tự Trung Quốc + Latin + số + gạch ngang
          slugControl.setValidators([
            Validators.required,
            Validators.pattern('^[\u4e00-\u9fa5a-z0-9-]+$'),
          ]);
        } else {
          // Tiếng Việt/Anh: chỉ Latin + số + gạch ngang
          slugControl.setValidators([Validators.required, Validators.pattern('^[a-z0-9-]+$')]);
        }

        if (nameControlValue) {
          const slug = this.createSlug(nameControlValue);
          slugControl.setValue(slug);
        }
      } else {
        valueControl.clearValidators();
        slugControl.clearValidators();
        slugControl.setValue('');
      }

      valueControl.updateValueAndValidity();
      slugControl.updateValueAndValidity();
    });

    // ✅ Auto-generate slug khi nhập name
    this.systemForm.get('name').valueChanges.subscribe((name) => {
      if (this.systemForm.get('type').value === 'policy' && name) {
        const slug = this.createSlug(name);
        this.systemForm.get('slug').setValue(slug);
      }
    });

    // Nếu là update, load data
    if (this.type === 'updateGroup' || this.type === 'updateGroupChild') {
      this.loadDataForUpdate();
    }
  }

  config = {
    placeholder: 'Nhập nội dung chi tiết',
    tabsize: 2,
    height: 'auto',
    dialogsInBody: true,
    minHeight: '35vh',
    uploadImagePath: '/api/upload',
    focus: false,
    fontNamesIgnoreCheck: ['Helvetica', 'Arial', 'Roboto', 'Times'],
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      ['font', ['bold', 'italic', 'underline', 'clear']],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'picture', 'link', 'video', 'hr']],
    ],
  };

  postItem(obj: any) {
    console.log('POST request with data:', obj);
    this.system.apiSystemSettingPost(obj).subscribe(
      (res) => {
        console.log('POST response:', res);
        if (res.data) {
          this.dialogRef.close({
            success: true,
            data: res.data,
            lang: this.lang,
          });
        } else {
          this.dialogRef.close({
            success: false,
            data: [],
          });
        }
      },
      (error) => {
        console.error('POST error:', error);
        this.toastr.error('Có lỗi xảy ra khi lưu dữ liệu');
        this.dialogRef.close({
          success: false,
          data: [],
        });
      }
    );
  }

  putItem(id: string, obj: any) {
    console.log('PUT request to id:', id, 'with data:', obj);
    this.system.apiSystemSettingIdPut(id, obj).subscribe(
      (res) => {
        console.log('PUT response:', res);
        if (res.success) {
          this.dialogRef.close({
            success: true,
            data: res.data,
            lang: this.lang,
            type: this.type,
          });
        } else {
          this.dialogRef.close({
            success: false,
            data: [],
          });
        }
      },
      (error) => {
        console.error('PUT error:', error);
        this.toastr.error('Có lỗi xảy ra khi cập nhật dữ liệu');
        this.dialogRef.close({
          success: false,
          data: [],
        });
      }
    );
  }

  loadDataForUpdate() {
    if (!this.id) return;

    console.log('Loading data for id:', this.id, 'type:', this.type);

    this.system.apiSystemSettingIdGet(this.id).subscribe(
      (res) => {
        console.log('API response:', res);
        if (res.success && res.data) {
          const item = res.data;
          console.log('Loaded item data:', item);
          this.systemForm.patchValue({
            id: item.id,
            type: item.type,
            name: item.name,
            value: item.value,
            description: item.description,
            is_hidden: item.is_hidden,
            sort_order: item.sort_order != null ? item.sort_order : 0,
            parent_id: item.parent_id,
            page_location: item.page_location,
            slug: item.slug,
            language: item.language,
          });
        } else {
          console.error('API returned no data');
        }
      },
      (error) => {
        console.error('Error loading data:', error);
        this.toastr.error('Không thể tải dữ liệu để chỉnh sửa');
      }
    );
  }

  createNewGroup() {
    this.systemForm.markAllAsTouched();

    if (this.systemForm.invalid) {
      this.toastr.error('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    if (this.type === 'newGroup') {
      const obj = {
        parent_id: this.id,
        name: this.systemForm.get('name').value,
        description: this.systemForm.get('description').value || '',
        sort_order: this.getSortOrder(),
        language: this.lang,
        is_hidden: this.systemForm.get('is_hidden').value,
      };
      this.postItem(obj);
    } else if (this.type === 'newGroupChild') {
      const selectedType = this.systemForm.get('type').value;

      const obj = {
        parent_id: this.id,
        name: this.systemForm.get('name').value,
        type: selectedType || null,
        value: this.systemForm.get('value').value,
        sort_order: this.getSortOrder(),
        language: this.lang,
        is_hidden: this.systemForm.get('is_hidden').value,
        description: this.systemForm.get('description').value,
        slug: this.systemForm.get('slug').value, // ✅ Thêm slug
      };

      console.log('Creating child with data:', obj);
      this.postItem(obj);
    } else if (this.type === 'updateGroup') {
      const obj: any = {
        name: this.systemForm.get('name').value,
  sort_order: this.getSortOrder(),
        is_hidden: this.systemForm.get('is_hidden').value,
        language: this.lang,
        description: this.systemForm.get('description').value || '',
      };

      console.log('Updating group with data:', obj);
      this.putItem(this.id, obj);
    } else if (this.type === 'updateGroupChild') {
      const obj: any = {
        name: this.systemForm.get('name').value,
        type: this.systemForm.get('type').value,
        value: this.systemForm.get('value').value,
        is_hidden: this.systemForm.get('is_hidden').value,
        sort_order: this.getSortOrder(),
        language: this.lang,
        page_location: this.systemForm.get('page_location').value,
        slug: this.systemForm.get('slug').value,
        description: this.systemForm.get('description').value || '',
      };

      Object.keys(obj).forEach((key) => obj[key] === undefined && delete obj[key]);

      console.log('Updating child with data:', obj);
      this.putItem(this.id, obj);
    }
  }

  save() {
    return this.createNewGroup();
  }

  close() {
    this.dialogRef.close({ success: false });
  }

  // ✅ Hàm tạo slug theo ngôn ngữ
  private createSlug(text: string): string {
    if (this.lang === 'zh') {
      // Tiếng Trung: giữ nguyên ký tự, chỉ xử lý space và ký tự đặc biệt
      return text
        .toString()
        .trim()
        .replace(/\s+/g, '-') // Thay space bằng -
        .replace(/[^\u4e00-\u9fa5a-z0-9-]/gi, '') // Chỉ giữ Trung + Latin + số + gạch
        .replace(/\-\-+/g, '-') // Xóa -- thành -
        .replace(/^-+/, '') // Xóa - đầu
        .replace(/-+$/, ''); // Xóa - cuối
    } else {
      // Tiếng Việt/Anh: dùng logic cũ
      return this.slugify(text);
    }
  }

  // ✅ Hàm slugify cho tiếng Việt/Anh
  private slugify(text: string): string {
    if (!text) return '';
    const normalized = text
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') 
      .replace(/đ/gi, 'd'); 

    return normalized
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }

  private getSortOrder(): number {
    const raw = this.systemForm.get('sort_order').value;
    const parsed = Number(raw);

    if (!Number.isFinite(parsed) || parsed < 0) {
      return 0;
    }

    return Math.floor(parsed);
  }
}
